import React, { useState, useEffect, useCallback } from "react";
import ct from "class-types.macro";
import {
  Accessory,
  GroupingAttributes,
  GroupMember,
} from "~/common/Store/models/ProductListing";
import { GroupingConfigurationOption } from "~/common/Store/models/ProductPage";
import Modal, { Header, Footer, Body } from "~/common/ModalReact";
import Button from "~/common/Buttons";
import { AddToCartModal } from ".";
import getCustomizableProductUrl from "~/common/helpers/getCustomizableProductUrl";
import setWindowLocationHref from "~/common/navigation/setWindowLocationHref";
import fetchAccessories from "~/ProductDetails/Carousel/fetchAccessories";
import VirtualGroupingQuantitySelection from "./VirtualGroupingQuantitySelection";
import getMinBuyForQuantityDropdown from "~/ProductDetails/Pricing/AddToCart/getMinBuyForQuantityDropdown";
import { getSortedOptions } from "~/ProductDetails/VirtualGrouping/VirtualGroupingHelper";

interface Props extends AddToCartModal<"virtualGrouping"> {
  itemQuantityVisible?: boolean;
  groupingAttributes: GroupingAttributes;
  isCustomizable?: boolean;
  hasAccessories?: boolean;
  setAccessories?: React.Dispatch<React.SetStateAction<readonly Accessory[]>>;
  quantity: string;
  handleQuantityChange?: (quantity: string) => void;
  isProductListing?: boolean;
  previouslyPurchased?: boolean;
}

export default ({
  itemQuantityVisible = false,
  groupingAttributes,
  isOpen,
  closeModal,
  submitModal,
  isCustomizable = false,
  hasAccessories,
  setAccessories,
  quantity,
  handleQuantityChange,
  isProductListing = false,
  previouslyPurchased = false,
}: Props) => {
  useEffect(() => {
    import("./VirtualGroupingStyles.less");
  });
  interface UserSelectedOptions {
    [index: string]: string;
  }

  const [userSelectedOptions, setUserSelectedOptions] =
    useState<UserSelectedOptions>({});

  const [groupMemberBasedOnSelection, setGroupMemberBasedOnSelection] =
    useState<GroupMember | undefined>();

  const [showQuantityDropdown, setShowQuantityDropdown] = useState(false);

  const [addToCartDisabled, setAddToCartDisabled] = useState(false);

  const [memberIsCustomizable, setMemberIsCustomizable] = useState(false);

  type GroupingOptions = {
    [key: string]: { sortIndex: number; value: string }[];
  };

  const groupingOptions: GroupingOptions = {};

  groupingAttributes.groupMembers.forEach((groupMember) => {
    groupMember.configurationOptions.forEach((option) => {
      if (option.name !== null && option.name !== undefined) {
        if (groupingOptions[option.name] === undefined) {
          groupingOptions[option.name] = [];
        }

        if (
          groupingOptions[option.name]!.filter((o) => o.value === option.value)
            .length === 0
        ) {
          groupingOptions[option.name]!.push(option);
        }
      }
    });
  });

  const keys = Object.keys(groupingOptions);

  const [showError, setShowError] = useState(false);
  const groupingOptionsSorted: GroupingOptions = {};

  keys.forEach((key) => {
    groupingOptionsSorted[key] = getSortedOptions(groupingOptions[key]) ?? [];
  });

  const shouldDisplayRequiredCustomization = (groupMember?: GroupMember) => {
    if (!groupMember) {
      return false;
    }

    const { customizationRequired, isCustomizable, customizationUrl } =
      groupMember;

    return !!(customizationRequired && (isCustomizable || customizationUrl));
  };

  const getGroupMemberBasedOnSelection = useCallback(() => {
    const firstGroupMemberWithUserSelectedOption = (
      groupMember: GroupMember,
    ) => {
      const configValues = groupMember.configurationOptions.map(
        (option) => option.value,
      );

      return Object.values(userSelectedOptions).every((userSelectedValue) =>
        configValues.some((configValue) => configValue === userSelectedValue),
      );
    };

    if (Object.keys(userSelectedOptions).length === keys.length) {
      const groupMember = groupingAttributes.groupMembers.find(
        firstGroupMemberWithUserSelectedOption,
      );

      setMemberIsCustomizable(shouldDisplayRequiredCustomization(groupMember));

      setGroupMemberBasedOnSelection(groupMember);
      return groupMember;
    }

    return undefined;
  }, [groupingAttributes.groupMembers, keys.length, userSelectedOptions]);

  useEffect(() => {
    if (userSelectedOptions) {
      getGroupMemberBasedOnSelection();
    }
  }, [getGroupMemberBasedOnSelection, userSelectedOptions]);

  useEffect(() => {
    if (groupMemberBasedOnSelection) {
      const startQuantity = getMinBuyForQuantityDropdown({
        minBuy: groupMemberBasedOnSelection.minimumBuy,
        mustBuy: groupMemberBasedOnSelection.mustBuy,
      });

      if (!itemQuantityVisible || (itemQuantityVisible && startQuantity > 1)) {
        setShowQuantityDropdown(true);
      } else {
        setShowQuantityDropdown(false);
      }
    }
  }, [groupMemberBasedOnSelection, itemQuantityVisible]);

  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();

    if (groupMemberBasedOnSelection) {
      if (isCustomizable || memberIsCustomizable) {
        setWindowLocationHref(
          getCustomizableProductUrl(
            groupMemberBasedOnSelection.customizationUrl,
            groupMemberBasedOnSelection.itemNumber,
          ),
        );
        return;
      }

      let groupMemberHasAccessories = false;
      if (hasAccessories && setAccessories) {
        const accessoryData = await fetchAccessories(
          groupMemberBasedOnSelection.itemNumber,
        );
        if (
          !isProductListing ||
          accessoryData.some((accessory) => accessory.isRequired)
        ) {
          setAccessories(accessoryData);
          groupMemberHasAccessories = !!accessoryData.length;
        }
      }

      submitModal({
        virtualGrouping: {
          groupMemberItemNumber: groupMemberBasedOnSelection.itemNumber,
          hasAccessories: groupMemberHasAccessories,
        },
      });
    } else {
      setShowError(true);
    }
  };

  const hasMatchingSelection = (
    groupingConfigurationOptions: GroupingConfigurationOption[],
    selectedElement: HTMLSelectElement,
  ) => {
    return groupingConfigurationOptions.some((x) => {
      return (
        x.name === selectedElement.title && x.value === selectedElement.value
      );
    });
  };

  const hasMatchingSelections = (
    groupingConfigurationOptions: GroupingConfigurationOption[],
    selectedElements: HTMLSelectElement[],
  ) => {
    return selectedElements.every((selectedElement) =>
      hasMatchingSelection(groupingConfigurationOptions, selectedElement),
    );
  };

  const hasGroupMemberOptionWithCurrentSelections = (
    groupMember: GroupMember | undefined,
    selectElement: HTMLSelectElement,
    configurationOptionName: string,
    allSelects: HTMLSelectElement[],
  ) => {
    if (
      configurationOptionName &&
      groupMember !== undefined &&
      selectElement !== undefined
    ) {
      const allOtherSelects = allSelects.filter(
        (select) => select.value && select.title !== selectElement.title,
      );

      return (
        configurationOptionName === selectElement.title &&
        hasMatchingSelections(groupMember.configurationOptions, allOtherSelects)
      );
    }

    return false;
  };

  const getAvailableOptionsBasedOnSelections = (
    selectElement: HTMLSelectElement,
    allSelectElements: HTMLSelectElement[],
  ) => {
    let availableOtherOptions: string[] = [];

    if (groupingAttributes?.groupMembers?.length > 0) {
      availableOtherOptions = groupingAttributes.groupMembers
        .map((groupMember, groupIndex) => {
          return groupMember.configurationOptions
            .filter((configurationOption) =>
              hasGroupMemberOptionWithCurrentSelections(
                groupingAttributes.groupMembers[groupIndex],
                selectElement,
                configurationOption.name,
                allSelectElements,
              ),
            )
            .map((configurationOption) => configurationOption.value);
        })
        .flat();
    }

    return availableOtherOptions;
  };

  const getVirtualGroupElements = () => {
    const productOptionSelectsClassName = ".virtual-grouping-select";

    return [
      ...document.querySelectorAll<HTMLSelectElement>(
        productOptionSelectsClassName,
      ),
    ];
  };

  const toggleDisabledOptions = (
    theOtherSelect: HTMLSelectElement,
    availableOtherOptions: string[],
  ) => {
    if (theOtherSelect?.options?.length > 0) {
      for (const option of theOtherSelect.options) {
        if (!option.value) continue;
        if (availableOtherOptions.includes(option.value)) {
          option.removeAttribute("disabled");
        } else {
          option.setAttribute("disabled", "disabled");
        }
      }
    }
  };

  const disableUnavailableOptions = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const virtualGroupElements = getVirtualGroupElements();

    if (!virtualGroupElements?.length) {
      return;
    }

    const targetSelect = event.currentTarget;
    const nonTargetSelects = virtualGroupElements.filter(
      (select) => select !== targetSelect,
    );
    const allOptionSelectElements = [
      ...nonTargetSelects.filter((other) => other).map((other) => other),
      targetSelect,
    ];

    nonTargetSelects
      .filter((nonTargetSelect) => nonTargetSelect !== undefined)
      .forEach((nonTargetSelect) => {
        const availableOtherOptions = getAvailableOptionsBasedOnSelections(
          nonTargetSelect,
          allOptionSelectElements,
        );

        toggleDisabledOptions(nonTargetSelect, availableOtherOptions);
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      className={ct("w-11/12", "md:max-w-md")}
    >
      <Header closeModal={closeModal}>
        <h4 className={ct("flex", "text-base", "m-0", "ml-3", "md:text-xl")}>
          Product Options
        </h4>
      </Header>

      <Body>
        <div style={{ display: "block" }}>
          <div
            id="accessoriesListOverlayContainer"
            className="accessoriesList form"
          >
            <p className="small">This item has further options available.</p>
            {keys.map((key) => (
              <div key={key} className="form__group">
                <div
                  className={`form__control-container ${
                    showError && !userSelectedOptions[key] ? "error" : ""
                  }`}
                >
                  <select
                    defaultValue=""
                    value={userSelectedOptions[key]}
                    className="form__control virtual-grouping-select"
                    onChange={(e) => {
                      disableUnavailableOptions(e);
                      setUserSelectedOptions({
                        ...userSelectedOptions,
                        [key]: e.target.value,
                      });
                    }}
                    title={key}
                  >
                    <option value="" disabled>{`Select ${key}`}</option>
                    {}
                    {groupingOptionsSorted[key]!.map((option) => (
                      <option value={option.value} key={option.value}>
                        {option.value}
                      </option>
                    ))}
                  </select>
                  <label className="form__label">
                    {`${key} - `}
                    <span className="label__required">Required</span>
                  </label>
                  <i className="icon-down-micro form__control-icon form__control-icon--lg" />
                </div>
              </div>
            ))}
            {showQuantityDropdown && groupMemberBasedOnSelection && (
              <VirtualGroupingQuantitySelection
                quantity={quantity}
                minBuy={groupMemberBasedOnSelection.minimumBuy}
                mustBuy={groupMemberBasedOnSelection.mustBuy}
                unitOfMeasure={groupMemberBasedOnSelection.unitOfMeasurement}
                handleQuantityChange={handleQuantityChange}
                setAddToCartDisabled={setAddToCartDisabled}
              />
            )}
          </div>
        </div>
      </Body>
      <Footer>
        <Button
          color="light"
          className={ct("mr-3")}
          type="button"
          onClick={closeModal}
        >
          Cancel
        </Button>
        {isCustomizable || memberIsCustomizable ? (
          <Button
            disabled={addToCartDisabled}
            className={ct("pl-16", "relative")}
            color="purple"
            type="submit"
            onClick={handleSubmit}
          >
            <img
              className={ct(
                "bg-black-opaque",
                "box-border",
                "inline-block",
                "h-full",
                "p-2",
                "absolute",
                "top-0",
                "left-0",
                "text-white",
              )}
              src="//cdnimg.webstaurantstore.com/uploads/blog/2019/3/wand-icon.png"
              alt="Customize Now"
            />
            Customize Now
          </Button>
        ) : (
          <Button
            disabled={addToCartDisabled}
            color="red"
            type="submit"
            onClick={handleSubmit}
            data-testid="virtual-grouping-modal-addtocart-button"
            gtm-id={previouslyPurchased ? "BuyItAgainATC" : "AddToCartATC"}
          >
            Add To Cart
          </Button>
        )}
      </Footer>
    </Modal>
  );
};
